/**
 * WHAT THE PRICING TAB WORKS FROM: THE DRAWN FLOOR, NOT THE ROOM.
 *
 * Jacky, 18 September: *"when I get to product & pricing, i shouldnt select room, I should select category
 * floor I drawn on canvas, unless i didnt or needed adjust, I do it on Site Assessment tab"*. He sells
 * flooring by the floor — "Ground · Hard Floor" is the thing quoted — and a room-by-room picker made him
 * assemble that by hand from nine rooms. Adjusting a room still belongs on the Site assessment tab, which
 * is where the drawing is.
 *
 * So one entry per (drawn floor × the flooring he set in the canvas), carrying the whole floor's area
 * **including its wardrobes** (they are floor that gets laid and paid for). A floor with no flooring
 * chosen still appears, said plainly, because a floor that silently vanishes from the picker is a quote
 * that quietly leaves work out.
 *
 * It lives here rather than in the component because it is a pure function of the document — and a
 * component module imports Vite's env, which no test can load.
 */
import { roomArea } from './customer-proposal.js';
import { wasteFor, layPatternOf } from './waste.js';
/** The label a room with no drawn floor belongs to — the same words the builder uses elsewhere. */
const WHOLE_SITE = 'Whole site';
/**
 * WHAT THE PRICING TAB WORKS FROM: THE DRAWN FLOOR, NOT THE ROOM.
 *
 * Jacky, 18 September: *"when I get to product & pricing, i shouldnt select room, I should select category
 * floor I drawn on canvas, unless i didnt or needed adjust, I do it on Site Assessment tab"*. He sells
 * flooring by the floor — "Ground · Hard Floor" is the thing quoted — and a room-by-room picker made him
 * assemble that by hand from nine rooms. Adjusting a room still belongs on the Site assessment tab, which
 * is where the drawing is.
 *
 * So one entry per (drawn floor × the flooring he set in the canvas), carrying the whole floor's area
 * **including its wardrobes** (they are floor that gets laid and paid for). A floor with no flooring
 * chosen still appears, said plainly, because a floor that silently vanishes from the picker is a quote
 * that quietly leaves work out.
 *
 * The name is the grouping key the priced copy and the customer's lines already use, so nothing
 * downstream had to learn a second vocabulary.
 */
export function pricingRooms(doc) {
    const groups = new Map();
    for (const room of doc.assessment.rooms) {
        if (room.out_of_scope || room.is_void)
            continue;
        const floor = doc.assessment.areas.find(a => a.id === room.area_id)?.name?.trim() || '';
        // A room that belongs to no drawn floor is the WHOLE SITE — saying just its category would read as a
        // floor called "Hard Floor".
        const floorName = floor || WHOLE_SITE;
        // No category drawn for the room: it is still floor, and the entry says so rather than hiding it.
        const category = (room.flooring ?? '').trim();
        // A room the site measure gave a product to is priced with that product's zone, not with every room of
        // the same floor type — two carpets on one floor are two lines (18 Oct 2026). Rooms without one group as before.
        const product = room.product_id ? `:p:${room.product_id.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-')}` : '';
        const base = category && category !== WHOLE_SITE ? `${floorName} · ${category}` : floorName;
        const label = room.product_id && room.product_name ? `${base} · ${room.product_name}` : base;
        const id = `floor:${room.area_id || 'whole-site'}:${(category || 'unset').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-')}${product}`;
        const held = groups.get(id) ?? { id, name: label, area: 0 };
        // `roomArea` prefers dimensions, which only a rectangle has; an irregular room is its drawn area.
        // `area_m2` already includes any wardrobe the flooring runs under (the reader adds it there), so
        // nothing is added back here — that would count the wardrobes twice.
        held.area = held.area === null || roomArea(room) === null ? null : held.area + roomArea(room);
        groups.set(id, held);
    }
    return [...groups.values()];
}
/**
 * THE AREA THE ENGINE IS ASKED TO PRICE — measured, plus the allowance his rule gives the LAY.
 *
 * Jacky: *"area SQM need also wastage - use rule is < 50 SQM 7%, over 50 SQM 5% … Herringbon 20%, Chevron
 * 25% same % for all sizes. … customer will see we will charge in the end by actual."*
 *
 * IT IS APPLIED HERE, WHERE THE REQUEST IS BUILT, and not on the customer's copy — for a reason the
 * first cut got wrong: moving only the quantity the customer reads leaves the engine's money where it
 * was, so the same price spread over a bigger number prints a LOWER rate per m² than the person is being
 * charged. A discount nobody offered. The allowance has to reach the price, which means the engine has to
 * be asked to price the chargeable area; the copy then carries the sentence (`wasteNoticeId`).
 *
 * The pattern comes from the product itself — `parquet_form` on the catalogue row — so a herringbone
 * floor is not charged a straight lay's allowance just because nobody said otherwise.
 */
export function chargedArea(areaM2, parquetForm, rules) {
    const measured = Math.round(areaM2 * 100) / 100;
    const pattern = layPatternOf(parquetForm);
    // With no rules read there is no allowance to apply — and inventing one would price work at a rate
    // nobody set. The measured area is then simply the chargeable area.
    const waste = rules ? wasteFor(measured, pattern, rules) : 0;
    return { measuredM2: measured, waste, chargeableM2: Math.round(measured * (1 + waste) * 100) / 100, pattern };
}
//# sourceMappingURL=proposal-scope.js.map